import { network } from 'config/networks';
import { Key, Keplr as keplr, FeeCurrency, ChainInfo } from '@keplr-wallet/types';
import { displayToast, TToastType } from 'components/Toasts';
import { isMobile } from 'libs/utils';
import { OfflineDirectSigner, OfflineSigner } from '@cosmjs/proto-signing';

import { CosmosChainId, CosmosWallet, NetworkChainId, TokenItemType, WalletType } from '@oraichain/oraidex-common';
import { chainInfos } from 'config/chainInfos';
import { WALLET_ENUM } from 'components/ConnectWallet/constants';
import { EIP_EIP_STORAGE_KEY_ACC, MetamaskOfflineSigner } from './eip191';
import { getAddress, getAddressByEIP191 } from 'helpers';

export default class Keplr extends CosmosWallet {
  typeWallet: WalletType | 'eip191';

  constructor(type: WalletType | 'eip191' = 'keplr') {
    super();
    this.typeWallet = type;
  }

  async createCosmosSigner(chainId: CosmosChainId): Promise<OfflineSigner> {
    const keplr = await this.getKeplr();
    if (keplr) return await keplr.getOfflineSignerAuto(chainId);
    if (window.ethereum) return await MetamaskOfflineSigner.connect(window.ethereum, network.denom);
    throw new Error('You have to install Cosmos wallet first if you do not use a mnemonic to sign transactions');
  }

  disconnect() {
    // clear data?
  }

  // priority with owallet
  private get keplr(): keplr {
    if (this.typeWallet === 'owallet') {
      return window.owallet;
    } else if (this.typeWallet === 'keplr') {
      return window.keplr;
    }
    return null;
  }

  async getOfflineSigner(chainId: string): Promise<OfflineSigner | OfflineDirectSigner> {
    return this.keplr.getOfflineSignerAuto(chainId);
  }

  async getChainInfosWithoutEndpoints(): Promise<
    Array<{
      chainId: string;
      feeCurrencies: FeeCurrency[];
    }>
  > {
    return this.keplr.getChainInfosWithoutEndpoints();
  }

  async suggestChain(chainId: string) {
    const isEnableKeplr = await this.getKeplr();
    if (isEnableKeplr) {
      if (!window.keplr) return;
      const chainInfo = chainInfos.find((chainInfo) => chainInfo.chainId === chainId);

      // do nothing without chainInfo
      if (!chainInfo) return;
      // if there is chainInfo try to suggest, otherwise enable it
      if (!isMobile()) {
        await this.keplr.experimentalSuggestChain(chainInfo as ChainInfo);
      }
      await this.keplr.enable(chainId);
      if (isMobile()) return;
      const keplrChainInfos = await this.keplr.getChainInfosWithoutEndpoints();
      const keplrChain = keplrChainInfos.find((keplrChain) => keplrChain.chainId === chainInfo.chainId);
      if (!keplrChain) return;

      const findFeeCurrencies = keplrChain.feeCurrencies.find((fee) => fee.gasPriceStep);
      // check to update newest chain info
      if (keplrChain.bip44.coinType !== chainInfo.bip44.coinType || !keplrChain.feeCurrencies?.[0]?.gasPriceStep) {
        // displayToast(TToastType.TX_INFO, {
        //   message: `${keplrChain.chainName} has Keplr cointype ${keplrChain.bip44.coinType}, while the chain info config cointype is ${chainInfo.bip44.coinType}. Please reach out to the developers regarding this problem!`
        // });
      }
    }
  }

  async suggestToken(token: TokenItemType) {
    // suggestToken is for cosmosBased only
    if (token.contractAddress) {
      const keplr = await this.getKeplr();
      if (!keplr) {
        return displayToast(TToastType.KEPLR_FAILED, {
          message: 'You need to install Keplr to continue'
        });
      }

      await keplr.suggestToken(String(token.chainId), token.contractAddress);
    }
  }

  async getKeplr(): Promise<keplrType | undefined> {
    if (document.readyState === 'complete') {
      return this.keplr;
    }

    return new Promise((resolve) => {
      const documentStateChange = (event: Event) => {
        if (event.target && (event.target as Document).readyState === 'complete') {
          resolve(this.keplr);
          document.removeEventListener('readystatechange', documentStateChange);
        }
      };

      document.addEventListener('readystatechange', documentStateChange);
    });
  }

  async getKeplrKey(chainId?: string): Promise<Key | undefined> {
    chainId = chainId ?? network.chainId;
    if (!chainId) return undefined;

    const keplr = await this.getKeplr();
    if (keplr) {
      return keplr.getKey(chainId);
    }
  }

  async getKeplrAddr(chainId?: NetworkChainId): Promise<string | undefined> {
    // not support network.chainId (Oraichain)
    chainId = chainId ?? network.chainId;

    try {
      if (this.typeWallet === (WALLET_ENUM.EIP191 as any)) {
        // TODO: cache if type wallet is eip191 ( metamask cosmos )

        // use for universal swap from oraichain to EVM
        if (chainId === 'oraibridge-subnet-2') {
          const result = localStorage.getItem(EIP_EIP_STORAGE_KEY_ACC);
          const parsedResult = JSON.parse(result);
          const oraiAddress = parsedResult ? parsedResult.accounts[0].address : null;
          return getAddress(oraiAddress, 'oraib');
        }
        return getAddressByEIP191();
      }

      const isEnableKeplr = await this.getKeplr();
      if (isEnableKeplr && ['keplr', 'owallet'].includes(this.typeWallet)) {
        if (!this.keplr) throw new Error('Error: get window cosmos!');
        const { bech32Address } = await this.getKeplrKey(chainId);
        if (!bech32Address) throw Error('Not found address from keplr!');
        return bech32Address;
      }
      return null;
    } catch (ex) {
      console.log(ex, chainId);
    }
  }
}
